import React, { useState, useEffect, useMemo } from 'react';
import styles from './PrintModal.module.scss';
import GenericModal from 'components/UI/V3/Modals/GenericModal';
import Button from 'components/UI/V3/Button/Button';
import Checkbox from 'components/UI/V3/Forms/Checkbox';
import DateSelectorComponent from 'components/CreateBooking/DateSelector/DateSelectorSection/DateSelectorComponent';
import CalendarBooking from 'components/CreateBooking/DateSelector/DateSelectorSection/CalendarBooking';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { searchTasksThunk, TasksStatus } from '@spike/tasks-action';
import PrintTasksPreview from '../PrintTasksPreview/PrintTasksPreview';
import { Task } from '@spike/task-model';
import { PageResult } from '@spike/model';
import { Room } from '@spike/room-model';
import { RootState } from 'store';
import { showErrorThunk } from '@spike/notifications-action';
import PrintRoomSelect from '../PrintRoomSelect/PrintRoomSelect';

export interface PrintModalProps {
    category?: string;
    selectedTab: string;
    selectedMarketplaceIds?: Array<number>;
    initialRoomIds?: Array<number>;
    onClose: () => void;
    onAfterPreviewClose?: () => void;
}

const defaultTypesFor = (category?: string) => ({
    feeding: category === 'feeding',
    medication: category === 'medication',
    hydration: category === 'care',
    relief: category === 'care',
    addon: category === 'care'
});

const TYPE_NUMBERS: Array<{ key: 'feeding' | 'medication' | 'hydration' | 'relief' | 'addon'; value: number }> = [
    { key: 'feeding', value: 1 },
    { key: 'medication', value: 2 },
    { key: 'hydration', value: 3 },
    { key: 'relief', value: 4 },
    { key: 'addon', value: 5 }
];

export const PrintModal: React.FC<PrintModalProps> = ({
    category,
    selectedTab,
    selectedMarketplaceIds,
    initialRoomIds,
    onClose,
    onAfterPreviewClose
}) => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const tasks = useSelector<RootState, Task[]>(state => state.tasks.tasks);
    const tasksStatus = useSelector<RootState, TasksStatus>(state => state.tasks.status);
    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);

    const [selectedDate, setSelectedDate] = useState(moment());
    const [calendarExpanded, setCalendarExpanded] = useState(false);
    const [selectedTypes, setSelectedTypes] = useState(() => defaultTypesFor(category));
    const [selectedRoomIds, setSelectedRoomIds] = useState<Array<number>>(() => initialRoomIds ?? []);

    const [isPrinting, setIsPrinting] = useState(false);
    const [previewTasks, setPreviewTasks] = useState<Task[] | null>(null);

    const selectedTaskTypes = TYPE_NUMBERS.filter(t => selectedTypes[t.key]).map(t => t.value);

    const isPrintDisabled = selectedTaskTypes.length === 0;

    const toggleType = (type: keyof typeof selectedTypes, value: boolean) => {
        setSelectedTypes(prev => ({ ...prev, [type]: value }));
    };

    const handleSelectDate = (date: Date) => {
        setSelectedDate(moment(date));
        setCalendarExpanded(false);
    };

    const statusForSearch = selectedTab === 'all' ? undefined : selectedTab;

    const ShowError = (message: string) => {
        dispatch(showErrorThunk(message));
        setIsPrinting(false);
    };

    const ShowPrint = () => {
        if (selectedTaskTypes.length === 0) {
            ShowError('There are no tasks to print.');
            return;
        }

        const period = {
            from: selectedDate.clone().startOf('day'),
            to: selectedDate.clone().endOf('day')
        };

        setIsPrinting(true);
        dispatch(
            searchTasksThunk(
                apiClientWrapper,
                1,
                999,
                '',
                period,
                statusForSearch,
                selectedTaskTypes,
                selectedMarketplaceIds,
                selectedRoomIds
            )
        );
    };

    useEffect(() => {
        if (!isPrinting) return;

        if (tasksStatus === TasksStatus.SearchSuccess) {
            if (tasks.length === 0) {
                setIsPrinting(false);
                onAfterPreviewClose?.();
                ShowError('There are no tasks to print.');
            } else {
                setPreviewTasks(tasks);
                setIsPrinting(false);
            }
        }
        if (tasksStatus === TasksStatus.Error) {
            setIsPrinting(false);
            ShowError('We couldn’t load tasks to print. Please try again.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPrinting, tasksStatus, tasks]);

    const footer = (
        <div className={styles.actions}>
            <Button
                variant="black"
                label="Cancel"
                fullWidth
                onClick={onClose}
            />
            <Button
                variant="green"
                label="Print"
                fullWidth
                disabled={isPrintDisabled || isPrinting}
                onClick={ShowPrint}
            />
        </div>
    );

    const header = (
        <div className={styles.header}>
            <span className={styles.title}>Print Tasks</span>
            <span className={styles.subtitle}>Please select the date & categories you want to print.</span>
        </div>
    );

    const dateSelector = (
        <div className={styles.dateSelector}>
            <span className={styles.title}>Date</span>

            <div className={styles.sectionCalendar}>
                <span className={styles.text}>{selectedDate.format('ll')}</span>

                <DateSelectorComponent
                    id="print_task_date"
                    expanded={calendarExpanded}
                    className={styles.contentDate}
                    onExpand={() => setCalendarExpanded(prev => !prev)}
                    onClickAway={() => setCalendarExpanded(false)}
                >
                    <CalendarBooking
                        date={selectedDate}
                        onDaySelect={handleSelectDate}
                    />
                </DateSelectorComponent>
            </div>
        </div>
    );

    const categoriesSection = (
        <div className={styles.categories}>
            <div className={styles.column}>
                <Checkbox
                    label="Feeding"
                    checked={selectedTypes.feeding}
                    onChange={v => toggleType('feeding', v)}
                />
                <Checkbox
                    label="Medication"
                    checked={selectedTypes.medication}
                    onChange={v => toggleType('medication', v)}
                />
                <Checkbox
                    label="Hydration"
                    checked={selectedTypes.hydration}
                    onChange={v => toggleType('hydration', v)}
                />
                <Checkbox
                    label="Relief"
                    checked={selectedTypes.relief}
                    onChange={v => toggleType('relief', v)}
                />
                <Checkbox
                    label="Add-on"
                    checked={selectedTypes.addon}
                    onChange={v => toggleType('addon', v)}
                />
            </div>
        </div>
    );

    const roomOptions = useMemo(
        () => rooms.page.filter(room => room.id != null).map(room => ({ id: room.id!, label: room.name })),
        [rooms.page]
    );

    const roomsSection = (
        <div className={styles.roomsSection}>
            <span className={styles.title}>Rooms</span>
            <PrintRoomSelect
                options={roomOptions}
                selected={selectedRoomIds}
                onChange={setSelectedRoomIds}
            />
        </div>
    );

    return (
        <>
            {previewTasks && (
                <PrintTasksPreview
                    tasks={previewTasks}
                    date={selectedDate.format('L')}
                    category={category}
                    onClose={() => {
                        setPreviewTasks(null);
                        onAfterPreviewClose?.();
                    }}
                />
            )}

            <GenericModal
                className={styles.printModal}
                onClose={onClose}
                footer={footer}
            >
                <div className={styles.body}>
                    {header}
                    <div className={styles.content}>
                        {dateSelector}
                        {categoriesSection}
                        {roomsSection}
                    </div>
                </div>
            </GenericModal>
        </>
    );
};

export default PrintModal;
